import { useCallback, useEffect, useRef, useState } from "react";
import { deepEqual } from "@heraion/shared";

import { useLeaveGuardRegister, type LeaveGuard } from "../Targets/LeaveGuard";

// THE "leave an editor with unsaved edits" guard (#76; TG-1 was its first,
// inline, caller). Back asks for confirmation ONLY when the edited value
// differs from the baseline it opened with; an untouched editor leaves
// silently (a confirm that fires on nothing trains the user to ignore it).
//
// The baseline is a SNAPSHOT frozen ONCE, at the first render where `ready`
// is true: never a live read of the source (a background reload would move
// the target and misjudge dirty either way). `ready=false` (still loading,
// nothing to compare against) is never dirty. Compared with the shared
// deepEqual, strict about undefined: normalise at the call site if a value can
// carry `{a: undefined}` vs `{}`.
//
// Three exits all go through the same state: the editor's own back
// (`handleBack`), the host's hardware back (`registerBackGuard`) and a web
// shell tab change (LeaveGuardProvider via useLeaveGuardRegister). The guard
// handed out is read through refs so its once-registered identity always sees
// the CURRENT dirty state: a stale closure here would be the bug, silently.

export type UseUnsavedGuardInput<T> = {
  /** The edited value, compared against the frozen baseline. */
  value: T;
  /** False until `value` is the seeded value (nothing to compare yet). */
  ready?: boolean;
  /** Leave the editor (discard confirmed, or nothing to discard). */
  onLeave: () => void;
  /** Extra dirty signal for state outside `value` (e.g. a staged form not yet
   *  committed into it). OR-ed in, so back, hardware back and the web tab
   *  guard all see it. */
  extraDirty?: boolean;
  /** Host seam for a hardware-back handler. */
  registerBackGuard?: (guard: LeaveGuard) => void;
  /** #336: the baseline to start from instead of the first ready `value`. A
   *  draft resumed from the device starts AHEAD of the server, and must read
   *  as unsaved from its first frame. */
  initialBaseline?: T;
};

export function useUnsavedGuard<T>({
  value,
  ready = true,
  onLeave,
  extraDirty = false,
  registerBackGuard,
  initialBaseline,
}: UseUnsavedGuardInput<T>) {
  const baselineRef = useRef<{ v: T } | null>(null);
  if (ready && baselineRef.current === null)
    baselineRef.current = { v: initialBaseline !== undefined ? initialBaseline : value };
  const valueRef = useRef(value);
  valueRef.current = value;
  // #336: the value alone, without `extraDirty`: what a device draft of
  // `value` can actually keep.
  const valueDirty =
    baselineRef.current !== null && !deepEqual(value, baselineRef.current.v);
  const isDirty = extraDirty || valueDirty;
  const extraRef = useRef(extraDirty);
  extraRef.current = extraDirty;
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  const [confirming, setConfirming] = useState(false);
  // `proceed` (a shell tab change) is held until Discard; keep-editing drops it.
  const proceedRef = useRef<(() => void) | undefined>(undefined);

  const handleBack = useCallback(() => {
    if (dirtyRef.current) {
      setConfirming(true);
      return;
    }
    onLeaveRef.current();
  }, []);

  /** Realign the baseline to what was just persisted (`saved`, default: the
   *  latest value). Runs before the host's onSaved/onClose: those execute this
   *  tick, before a re-render could. */
  const markSaved = useCallback((saved?: T) => {
    baselineRef.current = { v: saved !== undefined ? saved : valueRef.current };
    dirtyRef.current = extraRef.current || !deepEqual(valueRef.current, baselineRef.current.v);
  }, []);

  const guard = useCallback<LeaveGuard>((proceed) => {
    if (!dirtyRef.current) return false;
    proceedRef.current = proceed;
    setConfirming(true);
    return true;
  }, []);
  useEffect(() => {
    registerBackGuard?.(guard);
  }, [registerBackGuard, guard]);
  const registerLeave = useLeaveGuardRegister();
  useEffect(() => (registerLeave ? registerLeave(guard) : undefined), [registerLeave, guard]);

  /** The value the editor is compared against (the server's, as far as the
   *  editor knows), or undefined before `ready`. */
  const getBaseline = useCallback(
    (): T | undefined => baselineRef.current?.v,
    [],
  );

  // Every exit leaves the same way: close the dialog, leave, then let a held
  // shell tab change proceed. #336: exposed so a caller can add its own exit
  // choices (the program editor's Save / Keep the draft) around the dialog.
  const leave = useCallback(() => {
    const proceed = proceedRef.current;
    proceedRef.current = undefined;
    setConfirming(false);
    onLeaveRef.current();
    proceed?.();
  }, []);
  /** Close the dialog without leaving and without dropping a held tab change
   *  (a choice still in flight, e.g. a Save). */
  const hideDialog = useCallback(() => setConfirming(false), []);
  const keepEditing = useCallback(() => {
    proceedRef.current = undefined;
    setConfirming(false);
  }, []);
  const dialogProps = {
    visible: confirming,
    onKeepEditing: keepEditing,
    onDiscard: leave,
  };

  return {
    isDirty,
    valueDirty,
    handleBack,
    markSaved,
    getBaseline,
    leave,
    hideDialog,
    dialogProps,
  };
}
